/**
 * F/FECTED Style Product Card Image Slider
 * Allows navigation through product images via arrows and dots
 */

(function() {
  'use strict';

  class ProductCardSlider {
    constructor(card) {
      this.card = card;
      this.figure = card.querySelector('.product-featured-image');
      this.imageLink = card.querySelector('.product-featured-image-link');
      this.primaryImage = card.querySelector('.product-primary-image');
      this.secondaryImages = Array.from(card.querySelectorAll('.product-secondary-image'));
      this.prevBtn = card.querySelector('.product-slider-nav--prev');
      this.nextBtn = card.querySelector('.product-slider-nav--next');
      this.dots = card.querySelectorAll('.product-slider-dot');
      
      this.currentIndex = 0;
      this.totalImages = this.secondaryImages.length + 1; // +1 for primary image
      
      if (this.totalImages <= 1) return;
      
      this.init();
    }
    
    init() {
      // Arrow navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigate(-1);
        });
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigate(1);
        });
      }
      
      // Dot navigation
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToSlide(index);
        });
      });
      
      // Touch/swipe support for mobile
      this.setupTouchEvents();
    }
    
    navigate(direction) {
      let newIndex = this.currentIndex + direction;
      
      if (newIndex < 0) {
        newIndex = this.totalImages - 1;
      } else if (newIndex >= this.totalImages) {
        newIndex = 0;
      }
      
      this.goToSlide(newIndex);
    }
    
    goToSlide(index) {
      // Reset all secondary images
      this.secondaryImages.forEach((img) => {
        if (img) {
          img.style.opacity = '0';
          img.classList.remove('is-active');
        }
      });
      
      if (index === 0) {
        // Show primary image
        if (this.primaryImage) {
          this.primaryImage.style.opacity = '1';
          this.primaryImage.classList.remove('is-hidden');
        }
      } else {
        // Hide primary, show secondary
        if (this.primaryImage) {
          this.primaryImage.style.opacity = '0';
          this.primaryImage.classList.add('is-hidden');
        }
        
        const targetSecondary = this.secondaryImages[index - 1];
        if (targetSecondary) {
          targetSecondary.style.opacity = '1';
          targetSecondary.classList.add('is-active');
        }
      }
      
      // Update current index
      this.currentIndex = index;
      
      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    }
    
    setupTouchEvents() {
      let startX = 0;
      let startY = 0;
      
      this.figure.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      
      this.figure.addEventListener('touchend', (e) => {
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        
        const diffX = startX - endX;
        const diffY = startY - endY;
        
        // Only trigger if horizontal swipe is greater than vertical
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.navigate(1); // Swipe left - next
          } else {
            this.navigate(-1); // Swipe right - prev
          }
        }
      }, { passive: true });
    }
  }
  
  // Initialize sliders
  function initSliders() {
    const productCards = document.querySelectorAll('.product-card');
    productCards.forEach(card => {
      // Check if slider already initialized and has nav buttons
      if (!card.dataset.sliderInit && card.querySelector('.product-slider-nav')) {
        card.dataset.sliderInit = 'true';
        new ProductCardSlider(card);
      }
    });
  }
  
  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }
  
  // Re-initialize when new products are loaded (AJAX pagination, etc.)
  const observer = new MutationObserver((mutations) => {
    let shouldInit = false;
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (node.classList?.contains('product-card') || node.querySelector?.('.product-card'))) {
            shouldInit = true;
          }
        });
      }
    });
    if (shouldInit) {
      setTimeout(initSliders, 100);
    }
  });
  
  // Observe the body for changes
  observer.observe(document.body, { childList: true, subtree: true });
  
  /**
 * Product Page Image Indicator
 * Shows which image is currently in view
 */
(function() {
  function initProductImageIndicator() {
    const productDetail = document.querySelector('.thb-product-detail');
    if (!productDetail) return;
    
    const slides = productDetail.querySelectorAll('.product-images__slide');
    if (slides.length <= 1) return;
    
    // Don't show on mobile
    if (window.innerWidth < 768) return;
    
    // Remove existing indicator if present
    const existingIndicator = document.querySelector('.product-image-indicator');
    if (existingIndicator) existingIndicator.remove();
    
    // Create indicator container
    const indicator = document.createElement('div');
    indicator.className = 'product-image-indicator';
    
    // Create dots for each slide
    slides.forEach((slide, index) => {
      const dot = document.createElement('div');
      dot.className = 'product-image-indicator-dot';
      if (index === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => {
        slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      indicator.appendChild(dot);
    });
    
    document.body.appendChild(indicator);
    
    // Update active dot on scroll using scroll position
    function updateIndicator() {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const viewportCenter = scrollTop + (windowHeight / 2);
      
      let activeIndex = 0;
      let closestDistance = Infinity;
      
      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideTop = scrollTop + rect.top;
        const slideCenter = slideTop + (rect.height / 2);
        const distance = Math.abs(viewportCenter - slideCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });
      
      const dots = indicator.querySelectorAll('.product-image-indicator-dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    }
    
    // Listen to scroll
    window.addEventListener('scroll', updateIndicator, { passive: true });
    
    // Initial update
    updateIndicator();
    
    // Hide indicator when scrolled past product section
    const imageContainer = productDetail.querySelector('.product-image-container');
    if (imageContainer) {
      const productObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          indicator.style.opacity = entry.isIntersecting ? '1' : '0';
          indicator.style.pointerEvents = entry.isIntersecting ? 'auto' : 'none';
        });
      }, {
        threshold: 0,
        rootMargin: '0px'
      });
      
      productObserver.observe(imageContainer);
    }
  }
  
  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductImageIndicator);
  } else {
    initProductImageIndicator();
  }
  
  // Reinitialize on resize
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      initProductImageIndicator();
    }, 250);
  });
})();
})();
